import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Activity, Server, Globe, AlertTriangle, RefreshCw, Sun, Moon, Clock, Route, LineChart as LineChartIcon, AreaChart as AreaChartIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ComposedChart
} from "recharts";
import MtrPanel from "@/components/MtrPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIME_PRESETS = [
  { label: "1 Saat", value: "1", hours: 1 },
  { label: "3 Saat", value: "3", hours: 3 },
  { label: "6 Saat", value: "6", hours: 6 },
  { label: "12 Saat", value: "12", hours: 12 },
  { label: "1 Gün", value: "24", hours: 24 },
];

const pairKey = (agentId, targetId) => `${agentId}|${targetId}`;

const PublicStatus = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [agents, setAgents] = useState([]);
  const [targets, setTargets] = useState([]);
  const [pingResults, setPingResults] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [theme, setTheme] = useState(() => localStorage.getItem("public-theme") || "light");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("public-view-mode") || "modern");
  // Cache-busts MRTG PNG URLs every 60s (matches the server-side PNG cache TTL)
  const [imageKey, setImageKey] = useState(() => Math.floor(Date.now() / 60000));
  const [filterAgent, setFilterAgent] = useState("all");
  const [filterTarget, setFilterTarget] = useState("all");

  // MTR state — keyed by `${agentId}|${targetId}`
  const [mtrOpen, setMtrOpen] = useState({});
  const [mtrCache, setMtrCache] = useState({});
  const [mtrPending, setMtrPending] = useState({});
  const [mtrError, setMtrError] = useState({});
  const mtrPendingRef = useRef({}); // requestId -> pairKey

  // In-flight abort controller for the dashboard fetch — prevents overlapping
  // requests from stacking up in the browser when the backend is slow.
  const abortRef = useRef(null);

  // Stable string key for the time range (avoids depending on the searchParams
  // object reference, which can change identity across renders even when the
  // underlying URL is unchanged).
  const timeRange = searchParams.get("range") || "2";

  const setTimeRange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("range", value);
    setSearchParams(newParams);
    setLoading(true);
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("public-theme", theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("public-view-mode", viewMode);
  }, [viewMode]);

  // Refresh MRTG image URLs every 60s while the tab is visible.
  useEffect(() => {
    if (viewMode !== "mrtg") return;
    const tick = () => {
      if (document.hidden) return;
      setImageKey(Math.floor(Date.now() / 60000));
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [viewMode, timeRange]);

  const fetchData = useCallback(async () => {
    // Cancel any previous in-flight request so we never stack up requests.
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    const REQUEST_TIMEOUT_MS = 60000;
    try {
      const [statusRes, pingRes, alertsRes] = await Promise.all([
        axios.get(`${API}/public/status`, { signal: controller.signal, timeout: REQUEST_TIMEOUT_MS }),
        axios.get(`${API}/public/ping-results?hours=${timeRange}`, { signal: controller.signal, timeout: REQUEST_TIMEOUT_MS }),
        axios.get(`${API}/public/alerts?limit=20`, { signal: controller.signal, timeout: REQUEST_TIMEOUT_MS }),
      ]);
      // Only commit results if this is still the most recent request.
      if (abortRef.current !== controller) return;
      setAgents(statusRes.data.agents);
      setTargets(statusRes.data.targets);
      setPingResults(pingRes.data);
      setAlerts(alertsRes.data);
      setLastUpdate(new Date());
      setFetchError(null);
      setLoading(false);
    } catch (error) {
      const isCancel =
        axios.isCancel?.(error) ||
        error.code === "ERR_CANCELED" ||
        error.name === "CanceledError" ||
        error.name === "AbortError";
      if (isCancel) {
        // Another fetch took over — don't touch state.
        return;
      }
      console.error("Failed to fetch data:", error);
      // Keep old data on the screen instead of blanking to "No Monitoring Data".
      // Show an error banner so the user knows refresh failed.
      const isTimeout = error.code === "ECONNABORTED" || /timeout/i.test(error.message || "");
      setFetchError(
        isTimeout
          ? "Sunucu yanıt vermiyor (timeout). Yeniden deneniyor..."
          : "Veriler yüklenemedi. Bağlantı kontrol ediliyor..."
      );
      // Only flip loading off if we have at least some prior data; otherwise
      // keep the spinner so the user knows we're still trying.
      // (initial cold-start failure will keep showing the spinner)
      // Caveat: if backend is permanently down, the user will see the spinner
      // until they navigate away. That's acceptable for a status page.
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  }, [timeRange]);

  // Single consolidated fetch + auto-refresh effect
  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      if (document.hidden) return;
      fetchData();
    }, 30000);
    return () => {
      clearInterval(interval);
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [fetchData]);

  // Pre-index ping results by (agentId|targetId) for O(1) lookup per card
  const indexedResults = useMemo(() => {
    const map = new Map();
    for (const r of pingResults) {
      const key = pairKey(r.agent_id, r.target_id);
      let bucket = map.get(key);
      if (!bucket) {
        bucket = [];
        map.set(key, bucket);
      }
      bucket.push(r);
    }
    // Each bucket is already sorted ascending by the server
    return map;
  }, [pingResults]);

  // Compute per-card chart+stats once when results change
  const cardData = useMemo(() => {
    const out = new Map();
    for (const [key, points] of indexedResults.entries()) {
      const chartData = points.map((p) => {
        const t = new Date(p.timestamp);
        const timeLabel = `${t.getHours().toString().padStart(2, "0")}:${t
          .getMinutes()
          .toString()
          .padStart(2, "0")}`;
        const minR = p.min_ms != null ? Math.round(p.min_ms * 100) / 100 : null;
        const maxR = p.max_ms != null ? Math.round(p.max_ms * 100) / 100 : null;
        // bandHeight stacks on top of `min` to draw the MRTG/SmokePing-style
        // smoke band between min and max for a single time bucket.
        const bandHeight = minR != null && maxR != null ? Math.max(0, maxR - minR) : 0;
        // Per-bucket jitter proxy for the MRTG blue spikes overlay
        const jitter = minR != null && maxR != null ? Math.round(((maxR - minR) / 2) * 100) / 100 : 0;
        return {
          time: timeLabel,
          timestamp: t.getTime(),
          latency: p.latency_ms != null ? Math.round(p.latency_ms * 100) / 100 : null,
          min: minR,
          max: maxR,
          bandHeight,
          jitter,
          loss: p.loss_percent != null ? Math.round(p.loss_percent * 10) / 10 : 0,
        };
      });

      // Stats from buckets: avg over all samples, min of mins, max of maxes, p95 of bucket avgs
      const latencies = points.map((p) => p.latency_ms).filter((v) => v != null);
      let stats;
      if (latencies.length === 0) {
        stats = { avg: null, min: null, max: null, p95: null, current: null, loss: 0, jitter: 0 };
      } else {
        let totalSamples = 0;
        let weightedSum = 0;
        let lossWeighted = 0;
        let jitterSum = 0;
        let jitterCount = 0;
        for (const p of points) {
          const n = p.sample_count || 1;
          totalSamples += n;
          if (p.latency_ms != null) weightedSum += p.latency_ms * n;
          if (p.loss_percent != null) lossWeighted += (p.loss_percent / 100) * n;
          if (p.max_ms != null && p.min_ms != null) {
            // Jitter proxy: half of per-bucket min-max spread (approximates stddev of pings within bucket)
            jitterSum += (p.max_ms - p.min_ms) / 2;
            jitterCount += 1;
          }
        }
        const avg = weightedSum / totalSamples;
        const minMs = Math.min(...points.map((p) => (p.min_ms != null ? p.min_ms : p.latency_ms)).filter((v) => v != null));
        const maxMs = Math.max(...points.map((p) => (p.max_ms != null ? p.max_ms : p.latency_ms)).filter((v) => v != null));
        const sortedLat = [...latencies].sort((a, b) => a - b);
        const p95Idx = Math.floor(sortedLat.length * 0.95);
        const p95 = sortedLat[p95Idx] ?? sortedLat[sortedLat.length - 1];
        const current = points[points.length - 1]?.latency_ms ?? null;
        stats = {
          avg: Math.round(avg * 100) / 100,
          min: Math.round(minMs * 100) / 100,
          max: Math.round(maxMs * 100) / 100,
          p95: Math.round(p95 * 100) / 100,
          current: current != null ? Math.round(current * 100) / 100 : null,
          loss: totalSamples > 0 ? Math.round((lossWeighted / totalSamples) * 1000) / 10 : 0,
          jitter: jitterCount > 0 ? Math.round((jitterSum / jitterCount) * 100) / 100 : 0,
        };
      }
      out.set(key, { chartData: chartData.slice(-120), stats });
    }
    return out;
  }, [indexedResults]);

  const toggleMtr = useCallback(async (agentId, targetId) => {
    const key = pairKey(agentId, targetId);
    setMtrOpen((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      return next;
    });
    // Load latest cached MTR on first open if not already loaded
    if (!mtrOpen[key] && !mtrCache[key]) {
      try {
        const res = await axios.get(
          `${API}/public/mtr-results/latest?agent_id=${agentId}&target_id=${targetId}`
        );
        if (res.data && res.data.timestamp) {
          setMtrCache((prev) => ({ ...prev, [key]: res.data }));
        }
      } catch (e) {
        console.error("Failed to load latest MTR:", e);
      }
    }
  }, [mtrOpen, mtrCache]);

  const requestMtrRefresh = useCallback(async (agentId, targetId) => {
    const key = pairKey(agentId, targetId);
    setMtrPending((prev) => ({ ...prev, [key]: true }));
    setMtrError((prev) => ({ ...prev, [key]: null }));
    try {
      const res = await axios.post(`${API}/public/mtr-refresh`, {
        agent_id: agentId,
        target_id: targetId,
      });
      if (res.data.status === "agent_offline") {
        setMtrError((prev) => ({ ...prev, [key]: "Agent çevrimdışı." }));
        setMtrPending((prev) => ({ ...prev, [key]: false }));
        return;
      }
      const requestId = res.data.request_id;
      if (!requestId) {
        setMtrPending((prev) => ({ ...prev, [key]: false }));
        return;
      }
      mtrPendingRef.current[requestId] = key;

      // Polling fallback in case WS doesn't deliver in time
      const startedAt = Date.now();
      const poll = async () => {
        if (Date.now() - startedAt > 30000) {
          setMtrPending((prev) => ({ ...prev, [key]: false }));
          setMtrError((prev) => ({ ...prev, [key]: "Zaman aşımı — sonuç gelmedi." }));
          delete mtrPendingRef.current[requestId];
          return;
        }
        if (!mtrPending[key] && mtrCache[key]?.request_id === requestId) return;
        try {
          const pollRes = await axios.get(`${API}/public/mtr-refresh/${requestId}`);
          if (pollRes.data.completed && pollRes.data.result) {
            setMtrCache((prev) => ({ ...prev, [key]: pollRes.data.result }));
            setMtrPending((prev) => ({ ...prev, [key]: false }));
            delete mtrPendingRef.current[requestId];
            return;
          }
        } catch (e) {
          // 404 means expired — stop polling
          if (e.response?.status === 404) {
            setMtrPending((prev) => ({ ...prev, [key]: false }));
            delete mtrPendingRef.current[requestId];
            return;
          }
        }
        setTimeout(poll, 2000);
      };
      setTimeout(poll, 2000);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 429) {
        setMtrError((prev) => ({ ...prev, [key]: detail || "Çok sık istek." }));
      } else {
        setMtrError((prev) => ({ ...prev, [key]: "MTR isteği başarısız." }));
      }
      setMtrPending((prev) => ({ ...prev, [key]: false }));
    }
  }, [mtrPending, mtrCache]);

  // Public WebSocket — receive fresh MTR results pushed by the server
  useEffect(() => {
    if (!BACKEND_URL) return;
    const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws/public`;
    let ws;
    let reconnectTimer;
    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg.type !== "mtr_result" || !msg.data) return;
            const d = msg.data;
            const key = pairKey(d.agent_id, d.target_id);
            setMtrCache((prev) => ({ ...prev, [key]: d }));
            if (msg.request_id && mtrPendingRef.current[msg.request_id]) {
              setMtrPending((prev) => ({ ...prev, [key]: false }));
              delete mtrPendingRef.current[msg.request_id];
            }
          } catch (e) {
            // ignore parse errors
          }
        };
        ws.onclose = () => {
          reconnectTimer = setTimeout(connect, 5000);
        };
        ws.onerror = () => {
          try { ws.close(); } catch (e) {}
        };
      } catch (e) {
        reconnectTimer = setTimeout(connect, 5000);
      }
    };
    connect();
    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        ws.onclose = null;
        try { ws.close(); } catch (e) {}
      }
    };
  }, []);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            <p className="text-green-400">
              Avg: <span className="font-mono font-bold">{data.latency?.toFixed(2) || 'N/A'}</span> ms
            </p>
            {data.min !== null && (
              <p className="text-emerald-400">
                Min: <span className="font-mono">{data.min?.toFixed(2)}</span> ms
              </p>
            )}
            {data.max !== null && (
              <p className="text-red-400">
                Max: <span className="font-mono">{data.max?.toFixed(2)}</span> ms
              </p>
            )}
            {data.loss > 0 && (
              <p className="text-orange-500 font-bold">
                Loss: <span className="font-mono">{data.loss}%</span>
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  // Sorted by target first, then agent, with filters applied
  const combinations = useMemo(() => {
    const out = [];
    targets.forEach((target) => {
      if (filterTarget !== "all" && target.id !== filterTarget) return;
      agents.forEach((agent) => {
        if (filterAgent !== "all" && agent.id !== filterAgent) return;
        out.push({ agent, target });
      });
    });
    return out;
  }, [targets, agents, filterAgent, filterTarget]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col items-center justify-center gap-4 p-6 text-center">
        <div className="spinner"></div>
        <p className="text-sm text-muted-foreground">Veriler yükleniyor...</p>
        {fetchError && (
          <p className="text-sm text-red-500 max-w-md">{fetchError}</p>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="public-status-page">
      <header className="sticky top-0 z-50 bg-[hsl(var(--sidebar-bg))] border-b border-[hsl(var(--border))] px-4 md:px-8 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <a
            href="https://www.netlen.com.tr"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <img
              src="/netlen-logo.svg"
              alt="Netlen"
              className={`h-10 w-auto ${theme === 'dark' ? 'invert brightness-0 invert' : ''}`}
            />
            <div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Latency Monitor
              </p>
            </div>
          </a>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filterAgent} onValueChange={setFilterAgent}>
              <SelectTrigger className="w-[140px]">
                <Server className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Kaynak" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kaynaklar</SelectItem>
                {agents.map(agent => (
                  <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterTarget} onValueChange={setFilterTarget}>
              <SelectTrigger className="w-[140px]">
                <Globe className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Hedef" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Hedefler</SelectItem>
                {targets.map(target => (
                  <SelectItem key={target.id} value={target.id}>{target.name || target.hostname}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setViewMode(viewMode === 'modern' ? 'mrtg' : 'modern')}
              data-testid="view-mode-toggle"
              title={viewMode === 'modern' ? 'MRTG/SmokePing görünümüne geç' : 'Modern görünüme geç'}
            >
              {viewMode === 'modern' ? (
                <AreaChartIcon className="w-4 h-4" />
              ) : (
                <LineChartIcon className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">{viewMode === 'modern' ? 'MRTG' : 'Modern'}</span>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button onClick={fetchData} variant="outline" size="sm" className="gap-2">
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Yenile</span>
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-[hsl(var(--border))] flex-wrap">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <div className="flex flex-wrap gap-1">
            {TIME_PRESETS.map(preset => (
              <Button
                key={preset.value}
                variant={timeRange === preset.value ? "default" : "ghost"}
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setTimeRange(preset.value)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </div>
      </header>

      <main className="p-4 md:p-6 space-y-6">
        {fetchError && (
          <div className="rounded-md border border-red-500/50 bg-red-500/10 px-4 py-2 text-sm text-red-500">
            {fetchError}
          </div>
        )}
        {combinations.length > 0 ? (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            {combinations.map(({ agent, target }) => {
              const key = pairKey(agent.id, target.id);
              const { chartData = [], stats = { avg: null, min: null, max: null, p95: null, current: null, loss: 0 } } =
                cardData.get(key) || {};
              const LATENCY_THRESHOLD = 100;
              const isHealthy = stats.current === null || stats.current < LATENCY_THRESHOLD;
              const isOpen = !!mtrOpen[key];

              return (
                <Card key={key} className="glass-card">
                  <CardHeader className="pb-1 pt-3 px-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <div className={`w-2 h-2 rounded-full ${isHealthy ? 'bg-green-500' : 'bg-red-500'}`} />
                          <span className="font-medium text-sm text-foreground">{agent.name}</span>
                          <span className="text-muted-foreground text-xs">→</span>
                          <span className="text-cyan-500 font-medium text-sm">{target.name || target.hostname}</span>
                        </div>
                      </div>
                      {stats.current !== null && (
                        <div className="text-right">
                          <p className={`text-lg font-mono font-bold ${isHealthy ? 'text-foreground' : 'text-red-500'}`}>
                            {stats.current}
                            <span className="text-xs text-muted-foreground ml-0.5">ms</span>
                          </p>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="px-3 pb-3">
                    <div className={viewMode === 'mrtg' ? "w-full aspect-[5/2] mb-3" : "h-[120px] mb-3"}>
                      {viewMode === 'mrtg' ? (
                        <img
                          src={`${API}/public/mrtg-image?agent_id=${agent.id}&target_id=${target.id}&hours=${timeRange}&_r=${imageKey}`}
                          alt={`${agent.name} → ${target.name || target.hostname}`}
                          className="w-full h-full object-contain bg-white rounded"
                          loading="lazy"
                        />
                      ) : chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                              <defs>
                                <linearGradient id={`pub-gradient-${agent.id}-${target.id}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.4}/>
                                  <stop offset="50%" stopColor="#22c55e" stopOpacity={0.15}/>
                                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0}/>
                                </linearGradient>
                              </defs>
                              <CartesianGrid
                                strokeDasharray="1 3"
                                stroke="hsl(var(--border))"
                                strokeOpacity={0.3}
                                vertical={false}
                              />
                              <XAxis
                                dataKey="time"
                                stroke="hsl(var(--muted-foreground))"
                                fontSize={10}
                                tickLine={false}
                                axisLine={false}
                                interval="preserveStartEnd"
                                minTickGap={30}
                              />
                              <YAxis
                                stroke="hsl(var(--muted-foreground))"
                                fontSize={10}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(v) => v?.toFixed(2)}
                                width={45}
                                domain={['dataMin - 1', 'dataMax + 1']}
                              />
                              <Tooltip content={<CustomTooltip />} />
                              {stats.avg && (
                                <ReferenceLine
                                  y={stats.avg}
                                  stroke="#3b82f6"
                                  strokeDasharray="4 4"
                                  strokeOpacity={0.5}
                                />
                              )}
                              <Area
                                type="monotone"
                                dataKey="latency"
                                stroke="#22c55e"
                                strokeWidth={2}
                                fill={`url(#pub-gradient-${agent.id}-${target.id})`}
                                dot={(props) => {
                                  const { cx, cy, payload } = props;
                                  if (payload && payload.loss && payload.loss > 0) {
                                    return (
                                      <circle
                                        cx={cx}
                                        cy={cy}
                                        r={5}
                                        fill="#ef4444"
                                        stroke="#fff"
                                        strokeWidth={2}
                                      />
                                    );
                                  }
                                  return null;
                                }}
                                activeDot={{ r: 5, fill: '#22c55e', stroke: '#fff', strokeWidth: 2 }}
                                connectNulls={true}
                                isAnimationActive={false}
                              />
                          </ComposedChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-full flex items-center justify-center text-muted-foreground">
                          <div className="text-center">
                            <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">Veri bekleniyor...</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {viewMode === 'mrtg' ? (
                      <div className="pt-2 border-t border-border flex items-center justify-around text-[11px] flex-wrap gap-y-1">
                        <div className="flex items-center gap-1.5">
                          <span className="inline-block w-3 h-3 bg-green-500 flex-shrink-0" />
                          <span className="text-foreground">latency</span>
                          <span className="font-mono font-semibold text-foreground">
                            {stats.avg !== null ? stats.avg : '--'}
                          </span>
                          <span className="text-muted-foreground">ms av</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="inline-block w-3 h-3 bg-blue-600 flex-shrink-0" />
                          <span className="text-foreground">jitter</span>
                          <span className="font-mono font-semibold text-foreground">
                            {stats.jitter !== null && stats.jitter !== undefined ? stats.jitter : '--'}
                          </span>
                          <span className="text-muted-foreground">ms av</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="inline-block w-3 h-3 bg-red-600 flex-shrink-0" />
                          <span className="text-foreground">loss</span>
                          <span className="font-mono font-semibold text-foreground">
                            {stats.loss !== null && stats.loss !== undefined ? stats.loss : '--'}
                          </span>
                          <span className="text-muted-foreground">% av</span>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-4 gap-1 pt-2 border-t border-border">
                        <div className="text-center">
                          <p className="text-[9px] text-muted-foreground uppercase">Min</p>
                          <p className="text-xs font-mono font-semibold text-green-500">
                            {stats.min !== null ? `${stats.min}` : '--'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-[9px] text-muted-foreground uppercase">Avg</p>
                          <p className="text-xs font-mono font-semibold text-blue-500">
                            {stats.avg !== null ? `${stats.avg}` : '--'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-[9px] text-muted-foreground uppercase">95th</p>
                          <p className="text-xs font-mono font-semibold text-yellow-500">
                            {stats.p95 !== null ? `${stats.p95}` : '--'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-[9px] text-muted-foreground uppercase">Max</p>
                          <p className="text-xs font-mono font-semibold text-red-500">
                            {stats.max !== null ? `${stats.max}` : '--'}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="mt-2 pt-2 border-t border-border flex items-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs gap-1"
                        onClick={() => toggleMtr(agent.id, target.id)}
                        data-testid={`mtr-toggle-${agent.id}-${target.id}`}
                      >
                        <Route className="w-3 h-3" />
                        MTR
                        <span className="ml-1 text-muted-foreground">{isOpen ? "▴" : "▾"}</span>
                      </Button>
                    </div>

                    {isOpen && (
                      <MtrPanel
                        data={mtrCache[key]}
                        pending={!!mtrPending[key]}
                        error={mtrError[key]}
                        onRefresh={() => requestMtrRefresh(agent.id, target.id)}
                      />
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="glass-card">
            <CardContent className="p-12">
              <div className="text-center text-muted-foreground">
                <Activity className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No Monitoring Data</h3>
                <p>Waiting for agents and targets to be configured.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {(() => {
          const filteredAlerts = alerts.filter(alert =>
            alert.type === 'threshold' ||
            alert.type === 'packet_loss' ||
            alert.type === 'latency'
          );
          return filteredAlerts.length > 0 && (
            <Card className="glass-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  Aktif Uyarılar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {filteredAlerts.slice(0, 5).map(alert => (
                    <div key={alert.id} className={`alert-item ${alert.severity}`}>
                      <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                        alert.severity === 'critical' ? 'text-red-400' :
                        alert.severity === 'warning' ? 'text-yellow-400' : 'text-blue-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{alert.message}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {alert.agent_name && `${alert.agent_name} • `}
                          {new Date(alert.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })()}
      </main>

      <footer className="p-4 md:p-6 border-t border-[hsl(var(--border))]">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <p>NetPing Network Monitor</p>
          <p>Last updated: {lastUpdate.toLocaleTimeString()}</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicStatus;
